import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

interface UseTextStreamResult {
  content: string
  isStreaming: boolean
  isLoading: boolean
  isError: boolean
  retry: () => void
}

type StreamFn = (onChunk: (chunk: string) => void, signal: AbortSignal) => Promise<void>

export function useTextStream(
  cacheKey: Array<string>,
  createStream: (() => StreamFn) | undefined,
): UseTextStreamResult {
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => ['textStream', ...cacheKey], [cacheKey])

  const [content, setContent] = useState<string>(() => {
    return queryClient.getQueryData<string>(queryKey) ?? ''
  })
  // Drawers unmount between opens, so this ref is fresh per mount.
  const hadCacheHit = useRef(!!content)
  const [isStreaming, setIsStreaming] = useState(() => !content && !!createStream)
  const [isError, setIsError] = useState(false)
  const abortControllerRef = useRef<AbortController | undefined>(undefined)

  const startStream = useCallback((streamFn: StreamFn) => {
    setContent('')
    setIsStreaming(true)
    setIsError(false)

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    let accumulated = ''

    streamFn(
      (chunk: string) => {
        accumulated += chunk
        setContent(accumulated)
      },
      abortController.signal,
    )
      .then(() => {
        if (abortController.signal.aborted) return
        setIsStreaming(false)
        queryClient.setQueryData(queryKey, accumulated)
      })
      .catch((error) => {
        if (error.name === 'AbortError') return
        setIsError(true)
        setIsStreaming(false)
      })
  }, [queryClient, queryKey])

  useEffect(() => {
    if (hadCacheHit.current || !createStream) return

    startStream(createStream())

    return () => {
      abortControllerRef.current?.abort()
      abortControllerRef.current = undefined
      setIsStreaming(false)
    }
  }, [createStream, startStream])

  const retry = useCallback(() => {
    if (!createStream) return
    abortControllerRef.current?.abort()
    // Clear cache so a fresh stream replaces any truncated/stale content
    queryClient.removeQueries({ queryKey, exact: true })
    hadCacheHit.current = false
    startStream(createStream())
  }, [createStream, startStream, queryClient, queryKey])

  return {
    content,
    isStreaming,
    isLoading: isStreaming && !content,
    isError,
    retry,
  }
}
